import { Fragment, useRef, useState } from 'react';

const ACCEPT_EXTS = '.doc,.docx,.txt';
const MAX_BYTES = 25 * 1024 * 1024;

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

const STATUS = {
  new: { label: 'New person', badge: 'BioBulk-badge--new', action: 'Add as new' },
  enrich: { label: 'Will add bio', badge: 'BioBulk-badge--enrich', action: 'Fill in bio' },
  existing_bio: { label: 'Already has bio', badge: 'BioBulk-badge--existing', action: 'Overwrite existing' },
};

export function BioBulkUpload({ onSubmit }) {
  const [step, setStep] = useState('select');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [summary, setSummary] = useState(null);
  const [expandedBios, setExpandedBios] = useState({});
  const fileRef = useRef(null);

  const resetAll = () => {
    setStep('select');
    setFile(null);
    setBusy(false);
    setError(null);
    setPreview(null);
    setDecisions({});
    setSummary(null);
    setExpandedBios({});
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleFileSelect = (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    if (picked.size > MAX_BYTES) {
      setError(`File too large (${formatBytes(picked.size)}). Maximum is 25 MB.`);
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setError(null);
    setFile(picked);
  };

  const clearFile = () => {
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handlePreview = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const data = await onSubmit({ type: 'bios', action: 'preview', file });
      setPreview(data);
      const init = {};
      for (const entry of data.entries) {
        init[entry.index] = {
          include: entry.matchStatus !== 'existing_bio',
          overwrite: false,
        };
      }
      setDecisions(init);
      setStep('preview');
    } catch (err) {
      setError(err.message || 'Preview failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleCommit = async () => {
    if (!file || !preview) return;
    setBusy(true);
    setError(null);
    try {
      const decisionList = preview.entries.map((entry) => ({
        index: entry.index,
        include: !!decisions[entry.index]?.include,
        overwrite: !!decisions[entry.index]?.overwrite,
      }));
      const data = await onSubmit({
        type: 'bios',
        action: 'commit',
        file,
        decisions: decisionList,
      });
      setSummary(data);
      setStep('done');
    } catch (err) {
      setError(err.message || 'Commit failed.');
    } finally {
      setBusy(false);
    }
  };

  // For new/enrich rows the checkbox just toggles `include`. For existing_bio
  // rows the one checkbox means "overwrite the existing bio" — include and
  // overwrite are tied together so the admin doesn't have to tick both.
  const toggleAction = (idx, isExisting) => {
    setDecisions((d) => {
      const cur = d[idx] || {};
      if (isExisting) {
        const next = !cur.include;
        return { ...d, [idx]: { include: next, overwrite: next } };
      }
      return { ...d, [idx]: { ...cur, include: !cur.include, overwrite: false } };
    });
  };

  const toggleBioExpand = (idx) => {
    setExpandedBios((x) => ({ ...x, [idx]: !x[idx] }));
  };

  const includedCount = preview
    ? Object.values(decisions).filter((d) => d?.include).length
    : 0;

  return (
    <div className="BulkUpload-typePane">
      {step === 'select' && (
        <SelectStep
          file={file}
          fileRef={fileRef}
          onPick={handleFileSelect}
          onClear={clearFile}
          onPreview={handlePreview}
          busy={busy}
          error={error}
        />
      )}

      {step === 'preview' && preview && (
        <PreviewStep
          preview={preview}
          decisions={decisions}
          onToggleAction={toggleAction}
          expandedBios={expandedBios}
          onToggleBioExpand={toggleBioExpand}
          includedCount={includedCount}
          onCommit={handleCommit}
          onStartOver={resetAll}
          busy={busy}
          error={error}
        />
      )}

      {step === 'done' && summary && (
        <DoneStep summary={summary} onStartOver={resetAll} />
      )}
    </div>
  );
}

function SelectStep({ file, fileRef, onPick, onClear, onPreview, busy, error }) {
  return (
    <>
      <p className="AdminPortal-sectionLead">
        Upload one artist-bio file ({ACCEPT_EXTS}) containing multiple
        biographies. Each entry is auto-matched against the people table;
        you&rsquo;ll review matches and pick which to ingest before anything
        is written.
      </p>

      {error && (
        <div className="AdminPortal-banner AdminPortal-banner--error" role="alert">
          {error}
        </div>
      )}

      <div className="BulkUpload-dropzone">
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT_EXTS}
          onChange={onPick}
          className="BulkUpload-fileInput"
          id="bulk-upload-input-bios"
        />
        <label htmlFor="bulk-upload-input-bios" className="BulkUpload-dropLabel">
          <span className="BulkUpload-dropIcon">+</span>
          <span>Click to select a bio file</span>
          <span className="BulkUpload-dropHint">
            Accepts {ACCEPT_EXTS} · one file per upload
          </span>
        </label>
      </div>

      {file && (
        <div className="BulkUpload-fileList">
          <div className="BulkUpload-fileListHeader">
            <strong>Selected:</strong>
            <button type="button" className="BulkUpload-clearBtn" onClick={onClear}>
              Clear
            </button>
          </div>
          <ul className="BulkUpload-fileListItems">
            <li className="BulkUpload-fileListItem">
              <span className="BulkUpload-fileListName">{file.name}</span>
              <span className="BulkUpload-fileListMeta">{formatBytes(file.size)}</span>
            </li>
          </ul>
        </div>
      )}

      <div className="AdminPortal-actions">
        <button
          type="button"
          className="ChatButton AdminPortal-submitBtn"
          onClick={onPreview}
          disabled={!file || busy}
        >
          {busy ? 'Parsing…' : 'Preview bios'}
        </button>
      </div>
    </>
  );
}

function PreviewStep({
  preview,
  decisions,
  onToggleAction,
  expandedBios,
  onToggleBioExpand,
  includedCount,
  onCommit,
  onStartOver,
  busy,
  error,
}) {
  const { entries, counts, warnings, fileName } = preview;
  return (
    <section className="AdminPortal-section">
      <div className="AdminPortal-sectionHeader">
        <h3 className="AdminPortal-sectionTitle">Bio preview</h3>
        <button
          type="button"
          className="BulkUpload-clearBtn"
          onClick={onStartOver}
        >
          Start over
        </button>
      </div>

      <div className="BulkUpload-ohSummary">
        <div className="BulkUpload-ohSummaryRow">
          <strong className="BulkUpload-ohSummaryLabel">File</strong>
          <span>{fileName}</span>
        </div>
        <div className="BulkUpload-ohSummaryRow">
          <strong className="BulkUpload-ohSummaryLabel">Entries</strong>
          <span>
            {counts.total} parsed ·{' '}
            <span className="BulkUpload-countNew">{counts.new} new</span>
            {' · '}
            {counts.enrich} will enrich
            {' · '}
            <span className="BulkUpload-countDup">
              {counts.existingBio} already have a bio
            </span>
          </span>
        </div>
      </div>

      {warnings && warnings.length > 0 && (
        <div className="AdminPortal-banner AdminPortal-banner--warning">
          <strong>Warnings</strong>
          <ul className="BulkUpload-warningList">
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="BioBulk-tableWrap">
        <table className="BioBulk-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Status</th>
              <th>Existing match</th>
              <th>Class</th>
              <th>Words</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => {
              const dec = decisions[e.index] || {};
              const isExisting = e.matchStatus === 'existing_bio';
              const isExpanded = !!expandedBios[e.index];
              return (
                <Fragment key={e.index}>
                  <tr className="BioBulk-row">
                    <td>{e.index + 1}</td>
                    <td>
                      <button
                        type="button"
                        className="BulkUpload-clearBtn"
                        onClick={() => onToggleBioExpand(e.index)}
                        title={isExpanded ? 'Hide bio' : 'Show bio'}
                      >
                        {e.name}
                      </button>
                      {e.warnings && e.warnings.length > 0 && (
                        <div className="BioBulk-rowWarn">
                          {e.warnings.join(' ')}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`BioBulk-badge ${STATUS[e.matchStatus].badge}`}>
                        {STATUS[e.matchStatus].label}
                      </span>
                    </td>
                    <td>
                      {e.matchedPersonName ? (
                        <span title={`matched via ${e.matchedVia}`}>
                          {e.matchedPersonName}
                          {e.matchedVia === 'normalized' && (
                            <span className="BioBulk-fuzzy"> (fuzzy)</span>
                          )}
                        </span>
                      ) : (
                        <span className="BioBulk-muted">—</span>
                      )}
                    </td>
                    <td>
                      {e.classYear ?? <span className="BioBulk-muted">—</span>}
                    </td>
                    <td>{e.wordCount}</td>
                    <td>
                      <label className="BioBulk-actionCell">
                        <input
                          type="checkbox"
                          checked={!!dec.include}
                          onChange={() => onToggleAction(e.index, isExisting)}
                          disabled={busy}
                        />
                        <span className="BioBulk-actionLabel">
                          {STATUS[e.matchStatus].action}
                        </span>
                      </label>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="BioBulk-bioRow">
                      <td colSpan={7}>
                        {isExisting && e.existingBio ? (
                          <div className="BioBulk-bioCompare">
                            <div className="BioBulk-bioPane">
                              <div className="BioBulk-bioPaneLabel">
                                Existing bio
                                {e.matchedPersonName && (
                                  <span className="BioBulk-bioPaneSub">
                                    {' '}· {e.matchedPersonName}
                                  </span>
                                )}
                              </div>
                              <div className="BioBulk-bioPreview">
                                {e.existingBio}
                              </div>
                            </div>
                            <div className="BioBulk-bioPane">
                              <div className="BioBulk-bioPaneLabel">
                                New bio from upload
                                {dec.include && (
                                  <span className="BioBulk-bioPaneSub BioBulk-bioPaneSub--warn">
                                    {' '}· will replace existing
                                  </span>
                                )}
                              </div>
                              <div className="BioBulk-bioPreview">{e.bio}</div>
                            </div>
                          </div>
                        ) : (
                          <div className="BioBulk-bioPreview">{e.bio}</div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {error && (
        <div className="AdminPortal-banner AdminPortal-banner--error" role="alert">
          {error}
        </div>
      )}

      <div className="AdminPortal-actions">
        <button
          type="button"
          className="ChatButton ChatButton--primary AdminPortal-submitBtn"
          onClick={onCommit}
          disabled={busy || includedCount === 0}
        >
          {busy
            ? 'Committing…'
            : `Commit ${includedCount} ${includedCount === 1 ? 'entry' : 'entries'}`}
        </button>
      </div>
    </section>
  );
}

function DoneStep({ summary, onStartOver }) {
  return (
    <section className="AdminPortal-section">
      <h3 className="AdminPortal-sectionTitle">Upload complete</h3>
      <div className="AdminPortal-banner AdminPortal-banner--success">
        Inserted <strong>{summary.inserted}</strong> new ·{' '}
        enriched <strong>{summary.enriched}</strong> ·{' '}
        overwrote <strong>{summary.overwritten}</strong> ·{' '}
        skipped <strong>{summary.skipped}</strong>.
        {summary.embeddingFailures > 0 && (
          <>
            {' '}
            <span className="BulkUpload-countDup">
              {summary.embeddingFailures} embedding
              {summary.embeddingFailures === 1 ? '' : 's'} failed
              (rows kept; can be backfilled).
            </span>
          </>
        )}
      </div>

      {summary.entries && summary.entries.length > 0 && (
        <div className="BioBulk-tableWrap">
          <table className="BioBulk-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Action</th>
                <th>Person ID</th>
                <th>Embed</th>
              </tr>
            </thead>
            <tbody>
              {summary.entries.map((e) => (
                <tr key={e.index}>
                  <td>{e.index + 1}</td>
                  <td>{e.name}</td>
                  <td>{e.action}</td>
                  <td>{e.personId ?? '—'}</td>
                  <td>
                    {e.embedFailed ? (
                      <span className="BioBulk-badge BioBulk-badge--existing">
                        failed
                      </span>
                    ) : e.action === 'skipped' ? (
                      <span className="BioBulk-muted">—</span>
                    ) : (
                      'ok'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="AdminPortal-actions">
        <button
          type="button"
          className="ChatButton AdminPortal-submitBtn"
          onClick={onStartOver}
        >
          Upload another
        </button>
      </div>
    </section>
  );
}
